import {
    getDocs,
    collection,
    query,
    orderBy,
    doc,
    getDoc,
    deleteDoc,
    addDoc,
    updateDoc,
    serverTimestamp,
    where,
    type UpdateData,
} from "firebase/firestore";
import { db, auth } from '../../../../firebase/config';
import { fetchCompaniesData, fetchCompanyBankId } from '../../../companies/service/companiesService';

function omitUndefinedFirestoreFields(obj: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
        if (v !== undefined) out[k] = v;
    }
    return out;
}

/**
 * Editable BAMS counts + response only. Avoids spreading Firestore doc metadata into writes.
 * `response` is null when unset (clears or leaves as null in Firestore).
 */
export function buildBamsHardwareMutablePayload(edited: Partial<BAMSHardware>): Record<string, unknown> {
    const n = (v: unknown): number => {
        if (typeof v === 'number' && Number.isFinite(v)) return Math.max(0, Math.trunc(v));
        if (typeof v === 'string' && v.trim() !== '') {
            const p = parseInt(v, 10);
            return Number.isFinite(p) ? Math.max(0, p) : 0;
        }
        const p = parseInt(String(v ?? ''), 10);
        return Number.isFinite(p) ? Math.max(0, p) : 0;
    };
    const r = edited.response;
    const response = r === 'Police' || r === 'QRF' ? r : null;
    return {
        response,
        noOfBamsSystem: n(edited.noOfBamsSystem),
        fixedPanicButton: n(edited.fixedPanicButton),
        remotePanic: n(edited.remotePanic),
        fixedFootPanic: n(edited.fixedFootPanic),
        pir: n(edited.pir),
        openingDoorContact: n(edited.openingDoorContact),
        smokeHeatDetectors: n(edited.smokeHeatDetectors),
        noHooterSounderBranch: n(edited.noHooterSounderBranch),
        noOfKeypad: n(edited.noOfKeypad),
        cablesWires: n(edited.cablesWires),
        other: n(edited.other),
    };
}

export type BAMSHardware = {
    id: string;
    bankId?: string;
    bankName?: string;
    branchId?: string;
    branchCode?: string;
    branchName?: string;
    region?: string;
    hardwareType?: 'BAMS';
    vendorName?: string;
    response?: 'Police' | 'QRF';
    noOfBamsSystem?: number;
    fixedPanicButton?: number;
    remotePanic?: number;
    fixedFootPanic?: number;
    pir?: number;
    openingDoorContact?: number;
    smokeHeatDetectors?: number;
    noHooterSounderBranch?: number;
    noOfKeypad?: number;
    cablesWires?: number;
    other?: number;
    installationDate?: string;
    installationStatus?: 'installed' | 'sent' | 'pending';
    location?: string;
    remarks?: string;
    installedBy?: string;
    createdAt?: any;
    updatedAt?: any;
    createdBy?: string;
    companyId?: string;
    [key: string]: any;
};

export type BAMSHardwareFormData = {
    branchId?: string;
    region?: string;
    vendorName?: string;
    response?: 'Police' | 'QRF';
    noOfBamsSystem?: number;
    fixedPanicButton?: number;
    remotePanic?: number;
    fixedFootPanic?: number;
    pir?: number;
    openingDoorContact?: number;
    smokeHeatDetectors?: number;
    noHooterSounderBranch?: number;
    noOfKeypad?: number;
    cablesWires?: number;
    other?: number;
    installationDate?: string;
    installationStatus?: 'installed' | 'sent' | 'pending';
    location?: string;
    remarks?: string;
};

/**
 * Fetch company ID for the current user (PSO or Bank user)
 */
export async function fetchCompanyId(uid: string, isBankUser: boolean = false): Promise<string | null> {
    try {
        return await fetchCompanyBankId(uid, isBankUser);
    } catch (error) {
        console.error('Error fetching company ID:', error);
        return null;
    }
}

/**
 * Fetch all BAMS hardware installations for a company
 */
export async function fetchBAMSHardware(companyId: string): Promise<BAMSHardware[]> {
    if (!companyId) {
        console.log('No company ID provided');
        return [];
    }

    try {
        const hardwareSnap = await getDocs(
            query(
                collection(db, "banks", companyId, "bams_hardware"),
                orderBy("createdAt", "desc")
            )
        );
        
        const hardwareList = hardwareSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as BAMSHardware[];

        return hardwareList;
    } catch (error) {
        console.error('Error fetching BAMS hardware:', error);
        throw error;
    }
}

/**
 * Create a new BAMS hardware installation
 */
export async function createBAMSHardware(
    companyId: string,
    hardwareData: Partial<BAMSHardware>
): Promise<string> {
    const uid = auth.currentUser?.uid;
    if (!uid || !companyId) {
        throw new Error('User ID or company ID not found');
    }

    try {
        // Fetch bank and branch details
        const branchDoc = await getDoc(doc(db, 'banks', companyId, 'branch', hardwareData.branchId || ''));
        const branchData = branchDoc.exists() ? branchDoc.data() : {};
        
        // Fetch location name if locationId exists
        let locationName = '';
        if (branchData.locationId) {
            try {
                const locationDoc = await getDoc(doc(db, 'locations', branchData.locationId));
                if (locationDoc.exists()) {
                    locationName = locationDoc.data().name || '';
                }
            } catch (err) {
                console.error('Error fetching location:', err);
            }
        }
        
        const bankDoc = await getDoc(doc(db, 'banks', companyId));
        const bankData = bankDoc.exists() ? bankDoc.data() : {};

        const newHardwareData = {
            ...hardwareData,
            hardwareType: 'BAMS',
            bankName: bankData.companyName || bankData.name || 'N/A',
            branchCode: branchData.branchCode || 'N/A',
            branchName: branchData.address || 'N/A',
            region: locationName || hardwareData.region || '',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: uid,
            companyId: companyId,
        };

        const docRef = await addDoc(collection(db, 'banks', companyId, 'bams_hardware'), newHardwareData);
        return docRef.id;
    } catch (error) {
        console.error('Error creating BAMS hardware:', error);
        throw error;
    }
}

/**
 * Update an existing BAMS hardware installation
 */
export async function updateBAMSHardware(
    companyId: string,
    hardwareId: string,
    updateData: Partial<BAMSHardware>
): Promise<void> {
    if (!companyId || !hardwareId) {
        throw new Error('Company ID or hardware ID not provided');
    }

    try {
        // If branch changed, update branch details
        if (updateData.branchId) {
            const branchDoc = await getDoc(doc(db, 'banks', companyId, 'branch', updateData.branchId));
            const branchData = branchDoc.exists() ? branchDoc.data() : {};
            updateData.branchCode = branchData.branchCode || updateData.branchCode;
            updateData.branchName = branchData.address || updateData.branchName;
            
            // Fetch location name if locationId exists
            if (branchData.locationId) {
                try {
                    const locationDoc = await getDoc(doc(db, 'locations', branchData.locationId));
                    if (locationDoc.exists()) {
                        updateData.region = locationDoc.data().name || updateData.region || '';
                    }
                } catch (err) {
                    console.error('Error fetching location:', err);
                }
            }
        }

        const docRef = doc(db, 'banks', companyId, 'bams_hardware', hardwareId);
        const payload = omitUndefinedFirestoreFields({
            ...updateData,
            updatedAt: serverTimestamp(),
        });
        await updateDoc(docRef, payload as unknown as UpdateData<BAMSHardware>);
    } catch (error) {
        console.error('Error updating BAMS hardware:', error);
        throw error;
    }
}

/**
 * Delete a BAMS hardware installation
 */
export async function deleteBAMSHardware(companyId: string, hardwareId: string): Promise<void> {
    if (!companyId || !hardwareId) {
        throw new Error('Company ID or hardware ID not provided');
    }

    try {
        await deleteDoc(doc(db, 'banks', companyId, 'bams_hardware', hardwareId));
    } catch (error) {
        console.error('Error deleting BAMS hardware:', error);
        throw error;
    }
}

/**
 * Fetch branches for a company
 */
export async function fetchBranches(companyId: string): Promise<any[]> {
    try {
        // Fetch locations
        const locSnap = await getDocs(collection(db, "locations"));
        const locList = locSnap.docs.map(d => ({ id: d.id, ...d.data() } as { id: string; name: string }));

        const branchesSnap = await getDocs(collection(db, 'banks', companyId, 'branch'));
        return branchesSnap.docs.map(doc => {
            const branchData = doc.data();
            return {
                id: doc.id,
                ...branchData,
                locationName: locList.find(l => l.id === branchData.locationId)?.name || "N/A"
            };
        });
    } catch (error) {
        console.error('Error fetching branches:', error);
        return [];
    }
}

/**
 * Fetch deployments for a company to get BAMS vendor names
 * Only returns vendors where company type is "BAMS"
 * @param companyId - The bank/company ID
 * @param uid - User ID (used to find bank context for fetching companies)
 * @param fetchAllDeployments - If true, fetch all deployments regardless of PSO (for vendor display)
 */
export async function fetchDeployments(companyId: string, uid: string, fetchAllDeployments: boolean = false): Promise<Map<string, string>> {
    try {
        // Fetch deployments from banks/{companyId}/companyDeployment
        const deploymentsSnap = await getDocs(collection(db, "banks", companyId, "companyDeployment"));
        const deployments = deploymentsSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as any[];

        // If fetchAllDeployments is true, don't filter by PSO (to show vendors for all branches)
        // Otherwise, filter by PSO ID (same as deploymentsService)
        let filteredDeployments = deployments;
        if (!fetchAllDeployments) {
            filteredDeployments = deployments.filter(deployment => deployment.psoId === uid);
        }

        // Fetch companies using the same method as deploymentsService
        // Use the companyId to determine if we should fetch as bank user
        const bankDoc = await getDoc(doc(db, "banks", companyId));
        const bankData = bankDoc.exists() ? bankDoc.data() : {};
        const isBankUserForCompanies = bankData.userId === uid || companyId === uid;
        const companies = await fetchCompaniesData(uid, isBankUserForCompanies);

        // Filter companies to only include BAMS companies
        const bamsCompanies = companies.filter(company => company.companyType === 'BAMS');

       
        // Create a map: branchId -> companyName (vendor name) - only for BAMS companies
        const vendorMap = new Map<string, string>();

        filteredDeployments.forEach(deployment => {
            if (deployment.branchId && deployment.companiesId) {
                // Only match if the company is a BAMS company
                const company = bamsCompanies.find(c => c.id === deployment.companiesId);
                if (company && company.companyName) {
                    // Use the enriched company name (already merged from global if available)
                    vendorMap.set(deployment.branchId, company.companyName);
                } else {
                }
            }
        });

        return vendorMap;
    } catch (error) {
        console.error('Error fetching deployments:', error);
        return new Map();
    }
}

